import { useCallback, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector  } from '@/store/hooks';
import { setCurrentPmSessionId } from '@/store/actions/projectdata.action';
import { APIConfig } from '@/constants';
import { CommonService } from '@/services';
import commonService from '@/services/common.service.ts';

export const usePmAgentApi = (
    projectId: string,
    onStreamEnd: () => void,
    onError: (errorMessage?: string) => void
) => {
    const dispatch = useAppDispatch();
    const currentPmSessionId = useAppSelector((state) => state.projectData.currentPmSessionId);
    const [pmConversationHistory, setPmConversationHistory] = useState<any>(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
    const sessionCreatedRef = useRef<boolean>(false);


    const sendPmAgentMessage = useCallback(async (chatInput: string) => {
        try {
            const jwtToken = CommonService._localStorage.getItem('jwtToken');
            if (!jwtToken) {
                onError('Authentication required');
                return false;
            }

            const AuthorizationHeaders = { Authorization: "Bearer " + jwtToken };

            const url = APIConfig.PM_AGENT_START.URL(projectId);

            // const formData = new FormData();
            // formData.append('prompt', chatInput);
            // formData.append('agent_type', 'pm_agent'); // Specify PM agent type
            // if (pmSessionId) {
            //     formData.append('session_id', pmSessionId);
            // }
            // if (imageFile) {
            //     formData.append('file', imageFile);
            // }
            // if (typeof chatMode === 'boolean') {
            //     formData.append('chat_mode', String(chatMode));
            // }

            const payload = {
                se_conversation_id: currentPmSessionId,
                prompt: chatInput,
            }

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...AuthorizationHeaders,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                // Handle 401 Unauthorized error
                if (response.status === 401) {
                    CommonService._communications.logoutSubject.next();
                    return false;
                }

                // Attempt to extract error message from the response body
                let errorMessage = `HTTP error ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData?.detail || errorData?.message || errorMessage;
                } catch (jsonError) {
                    console.error("Error parsing error response:", jsonError);
                    commonService._alert.showToast("Error processing request.", "error")
                }

                throw new Error(errorMessage);
            }

            // onStreamStart();

            // Handle streaming response
            const reader = response.body?.getReader();
            if (reader) {
                const decoder = new TextDecoder();
                let buffer = '';


                try {
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;

                        let newlineIndex;
                        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
                            const line = buffer.slice(0, newlineIndex).trim();
                            buffer = buffer.slice(newlineIndex + 1);

                            if (!line || !line.startsWith("data:")) continue;

                            try {
                                // Extract the JSON part from the line
                                const jsonStr = line.replace(/^data:\s*/, "");
                                const parsedData = JSON.parse(jsonStr);
                                // Handle error messages directly in the stream
                                if (parsedData.type === "error") {
                                    let errorMessage = "PM Agent Error Processing Request";

                                    try {   
                                        // Parse the nested error object that's in string format
                                        const nestedError = JSON.parse(parsedData.message.replace(/'/g, '"'));
                                        if (nestedError?.error?.message) {
                                            errorMessage = nestedError.error.message;
                                        }
                                    } catch (parseErr) {
                                        console.error("Error parsing nested error message:", parseErr);
                                        // If nested parsing fails, use the original message
                                        errorMessage = parsedData.message || errorMessage;
                                    }

                                    // Emit error event
                                    commonService._communications.handlePmResponseStreamerSubject.next(JSON.stringify({
                                        type: 'pm_agent.error',
                                        data: {
                                            error: errorMessage,
                                            session_id: currentPmSessionId,
                                        }
                                    }));
                                    
                                    onError(errorMessage);
                                    continue;
                                }

                                // Emit PM agent specific events to the communications service
                                commonService._communications.handlePmResponseStreamerSubject.next(line);
                                
                            } catch (err) {
                                console.error("Error processing PM Agent stream line:", line, err);
                            }
                        }
                    }
                } finally {
                    reader.releaseLock();
                }
            }

            // Emit completion event if stream ended without proper completion
            commonService._communications.handlePmResponseStreamerSubject.next(JSON.stringify({
                type: 'pm_agent.complete',
                data: {
                    session_id: currentPmSessionId,
                    completion_time: Date.now(),
                }
            }));
            onStreamEnd();
            return true;

        } catch (error: any) {
            console.error("PM Agent API error:", error);
            
            // Emit error event through communications service
            commonService._communications.handlePmResponseStreamerSubject.next(JSON.stringify({
                type: 'pm_agent.error',
                data: {
                    error: error.message || "Failed to send message to PM agent",
                    session_id: currentPmSessionId,
                }
            }));
            
            onError(error.message || "Failed to send message to PM agent");
            return false;
        }
    }, [projectId, onStreamEnd, onError, currentPmSessionId]);

    const createPmSession = useCallback(async() => {
        // Prevent creating multiple sessions
        if (sessionCreatedRef.current && currentPmSessionId) {
            return;
        }

        sessionCreatedRef.current = true;
        
        CommonService._api.post(APIConfig.CREATE_PM_SESSION.URL(projectId), {})
            .then((response: any) => {
                if (response?.data?.id) {
                    dispatch(setCurrentPmSessionId(response?.data?.id));
                } else {
                    sessionCreatedRef.current = false;
                    onError('No session_id returned from API');
                }
            })
            .catch((error: any) => {
                console.error("PM Session creation error:", error);
                sessionCreatedRef.current = false;
                onError(error?.message || "Failed to create PM session");
            });
    }, [projectId, onError, currentPmSessionId, dispatch]);

   
    const loadPmConversationHistory = useCallback(async () => {
            setIsLoadingHistory(true);
            CommonService._project_service.getProjectConversationHistory(projectId, {}) 
            .then((response: any) => {
                setPmConversationHistory(response?.data?.sessions || []);
                setIsLoadingHistory(false);
            })
            .catch((error: any) => {
                setIsLoadingHistory(false);
                commonService._alert.showToast(error.message || "Failed to load PM conversation history", "error");
            })
    }, [projectId]);

   

    return {
        sendPmAgentMessage,
        createPmSession,
        loadPmConversationHistory,
        pmSessionId: currentPmSessionId,
        pmConversationHistory,
        isLoadingHistory
    };
};
